import { Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/common";
import { useManageUserPageData } from "../hooks";
import OrganizationSelect from "./OrganizationSelect";
import PermissionOverridesSection from "./PermissionOverridesSection";

const ManageUserForm = observer(() => {
  const { t } = useTran();
  const { userId } = useParams();
  const {
    loading,
    errorMessage,
    handleRetry,
    editForm,
    roleOptions,
    loadingRoles,
    statusOptions,
    isCompanyAdmin,
    companyOptions,
    companyLoading,
    organizationTree,
    organizationLoading,
    permissionModules,
    saving,
    meta,
    formatDateTime,
    handleChange,
    handleSelectChange,
    handleCompanyChange,
    handleOrganizationChange,
    handlePermissionOverridesChange,
    handleSubmit,
  } = useManageUserPageData({ userId });

  if (!userId) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("Select a user to view details.")}
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("Loading user details...")}
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="space-y-3 rounded-radius border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <p>{errorMessage}</p>
        <Button type="button" variant="outline" size="sm" onClick={handleRetry}>
          {t("Try again")}
        </Button>
      </div>
    );
  }

  const emptyRoleLabel = t("No roles available.");
  const emptyCompanyLabel = t("No companies available");

  return (
    <form id="manage-user-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            {t("Full name")}
          </label>
          <Input
            value={editForm.name}
            onChange={handleChange("name")}
            placeholder={t("Enter full name")}
            autoComplete="name"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            {t("Username")}
          </label>
          <Input
            value={editForm.username}
            onChange={handleChange("username")}
            placeholder={t("Enter username")}
            autoComplete="username"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            {t("Email address")}
          </label>
          <Input
            type="email"
            value={editForm.email}
            onChange={handleChange("email")}
            placeholder={t("Enter email address")}
            autoComplete="email"
          />
        </div>
        <SelectField
          id="role"
          label={t("Role")}
          value={editForm.role || undefined}
          onChange={handleSelectChange("role")}
          options={roleOptions}
          placeholder={t("Select a role")}
          disabled={loadingRoles}
          emptyLabel={emptyRoleLabel}
        />
        <div className="space-y-2">
          <SelectField
            id="status"
            label={t("Status")}
            value={editForm.status || undefined}
            onChange={handleSelectChange("status")}
            options={statusOptions}
            placeholder={t("Select a status")}
          />
          <p className="text-xs text-muted-foreground">
            {t(
              "Set to Disabled to suspend access or Deleted to remove access entirely.",
            )}
          </p>
        </div>
        {isCompanyAdmin ? (
          <SelectField
            id="companyId"
            label={t("Company Name")}
            value={editForm.companyId || undefined}
            onChange={handleCompanyChange}
            options={companyOptions}
            placeholder={t("Select a company")}
            disabled={companyLoading}
            emptyLabel={emptyCompanyLabel}
          />
        ) : (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t("Organization")}
            </label>
            <OrganizationSelect
              value={editForm.organizationUnitId || undefined}
              onValueChange={handleOrganizationChange}
              disabled={organizationLoading}
              placeholder={t("Select an organization")}
              fallbackLabel={editForm.organizationUnitName}
              tree={organizationTree}
            />
          </div>
        )}
      </div>

      <PermissionOverridesSection
        value={editForm.permissionOverrides}
        modules={permissionModules}
        onChange={handlePermissionOverridesChange}
        disabled={saving}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("Created at")}
          </p>
          <p className="text-sm text-foreground">
            {formatDateTime(meta.createdAt)}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("Last updated")}
          </p>
          <p className="text-sm text-foreground">
            {formatDateTime(meta.updatedAt)}
          </p>
        </div>
      </div>
    </form>
  );
});

export default ManageUserForm;
